<template>
<div class="categories-component">
  <ul class="categories-component__categories">
    <template v-if="categoriesArray">
      <li v-for="c in categoriesArray"
          :key="c.c.id"
          class="categories-component__category"
          :class="{ [`categories-component__category--depth-${c.depth}`]: true }"
      >
        <router-link :to="{ name: 'catalog', params: { categoryId: c.c.short_id, categorySlug: c.c.slug, } }"
                     class="categories-component__category-link"
        >
          <div v-if="hasCoverMedia(c.c)" class="categories-component__category-image-wrapper">
            <responsive-image :media="c.c.media.cover"
                              :sizes="['large', 'medium']"
                              class="categories-component__category-image"
                              :alt="c.c.name"
            />
          </div>
          <h5 class="categories-component__category-name" v-if="shouldDisplayCategoryName">
            {{ c.c.name }}
          </h5>
        </router-link>
        <button v-if="(c.c.children || []).length > 0"
                class="categories-component__toggle-subcategories-button"
                @click="expandedCategories.includes(c.c.id) ? expandedCategories.splice(expandedCategories.indexOf(c.c.id), 1) : expandedCategories.push(c.c.id)"
                :aria-expanded="expandedCategories.includes(c.c.id).toString()"
        >
          <div class="categories-component__toggle-subcategories-button-content">
            <IconArrowDown/>
          </div>
        </button>
      </li>
    </template>
  </ul>
</div>
</template>

<script>/**
 * Transforms a catergory tree into a plain array with depth info.
 * @param {object[]} tree
 * @param {Function} isExpanded
 * @param {number} currDepth
 * @returns {Array<{ c: object, depth: number }>}
 */

function categoriesTreeToArray(tree, isExpanded, currDepth = 0) {
  let res = [];

  for (const parentCategory of tree) {
    res.push({ c: parentCategory, depth: currDepth });

    if (isExpanded(parentCategory)) {
      res = res.concat(categoriesTreeToArray(parentCategory.children || [], isExpanded, currDepth + 1));
    }
  }

  return res;
}

import ResponsiveImage from '@/components/images/ResponsiveImage'
import { mapGetters } from 'vuex'

export default {
  components: {
    ResponsiveImage,
    IconArrowDown: () => import('@/assets/images/icons/icon-arrow-down-small.svg?inline'),
  },

  props: {
    categories: {
      type: Array,
      required: true,
    },
  },

  computed: {
    ...mapGetters({
      categoriesSettings: 'settings/homeCategories',
    }),

    shouldDisplayCategoryName() {
      return this.categoriesSettings.display_names === 'always'
    },

    categoriesArray() {
      const tree = this.categories;

      if (!Array.isArray(tree)) {
        return null;
      }

      return categoriesTreeToArray(tree, c => this.expandedCategories.includes(c.id));
    },
  },

  methods: {
    hasCoverMedia(category) {
      return !!category.media && !!category.media.cover && !!category.media.cover.id
    },
  },

  data() {
    return {
      expandedCategories: [],
    };
  },
}
</script>

<style lang="less">
@r: categories-component;
.@{r} {
  padding-top: 1px; // So the page's margin that accoutns for the top bar and content margins don't collapse.
  &__categories {
    @apply flex flex-col -m-1;
  }
  @base-margin: theme('margin.1');
  &__category {
    @apply relative rounded-lg;
    margin: @base-margin;
  }
  &__category-link {}
  &__category-name {
    @apply font-light text-sm text-dark;
  }
  &__category-image {
    @apply rounded-lg;
    object-fit: cover;
  }
  &__toggle-subcategories-button {
    @apply px-4;
  }
  &__toggle-subcategories-button-content {
    @apply rounded-full bg-light w-6 h-6 flex items-center justify-center;
    transition: transform 0.15s ease-out;
    svg {
      @apply w-3 stroke-current text-dark-dark;
    }
  }
  &__toggle-subcategories-button[aria-expanded="true"] {
    .@{r}__toggle-subcategories-button-content {
      transform: rotate(-180deg);
    }
  }
  &__category--depth-0 {
    @apply h-32
      bg-primary-lighter; // For when the image is absent.
    .@{r}__category-link {
      @apply absolute inset-0;
    }
    .@{r}__category-image {
      @apply absolute inset-0 h-full w-full;
    }
    .@{r}__category-name {
      @apply absolute left-0 bottom-0 bg-white rounded py-1 px-2 m-4;
    }
    .@{r}__toggle-subcategories-button {
      @apply absolute right-0 h-full;
    }
  }
  &__category:not(&__category--depth-0) {
    @apply border border-grey-lighter flex;
    box-sizing: content-box;
    min-height: theme('height.12');
    .@{r}__category-link {
      @apply flex-grow flex;
    }
    .@{r}__category-image-wrapper {
      @apply w-10 m-1 mr-0 relative flex-shrink-0;
    }
    .@{r}__category-image {
      @apply absolute h-full w-full;
    }
    .@{r}__category-name {
      @apply self-center p-3;
    }
  }
  each(range(1,10), {
    .@{r}__category--depth-@{value} {
      margin-left: calc(@base-margin + theme('margin.4') * @value);
    }
  })
}
</style>
